import React, { useMemo, useState, memo } from "react";
import "./CaptureTable.css";
import satSpecs from "./sat_db/satSpecs";
import {
  getCloudCoverColor,
  getNonSarCloudCover,
  hasHourlyCloudCover,
  isSarCapture,
} from "./CaptureMetrics";

function TableRow({ item, index, handleRowMouseEnter, handleRowMouseLeave, cloudDataLoaded }) {
  const { id, timestamp, cloudCover, cloudCoverColor, sarCapture } = item;
  const cleanedTimestamp = timestamp.replace('+00:00', 'Z');
  const date = new Date(cleanedTimestamp);
  const formattedDate = date.toISOString().split('T')[0];
  const formattedTime = date.toISOString().split('T')[1].slice(0, 8);
  const spec = satSpecs[id] ? satSpecs[id] : {};
  const constellation = spec.constellation ? spec.constellation : "Unknown";
  const resolution = spec.resolution ? spec.resolution : "Unknown";
  const spectralType = spec.spectral_range ? spec.spectral_range : "Unknown";

  return (
    <tr
      key={index}
      className="table-row"
      onMouseEnter={() => handleRowMouseEnter(id, timestamp)}
      onMouseLeave={handleRowMouseLeave}
    >
      <td className="table-cell">{`${constellation} (${id})`}</td>
      <td className="table-cell">{`${resolution}m`}</td>
      <td className="table-cell">{spectralType}</td>
      <td
        className="table-cell"
        style={{
          backgroundColor: cloudCoverColor.backgroundColor,
          color: cloudCoverColor.textColor,
        }}
      >
        {sarCapture ? "N/A" : cloudCover === null ? (cloudDataLoaded ? "N/A" : "Loading") : `${cloudCover}%`}
      </td>
      <td className="table-cell">{`${formattedDate}, ${formattedTime}`}</td>
    </tr>
  );
}

const MemoizedTableRow = memo(TableRow);

function CaptureTable({ boundingBoxesData, cloudData, handleRowMouseEnter, handleRowMouseLeave }) {
  const [sortKey, setSortKey] = useState("cloudCover");
  const cloudDataLoaded = hasHourlyCloudCover(cloudData.hourly);

  const sortedBoundingBoxesData = useMemo(() => {
    const boundingBoxesDataWithCloudCover = boundingBoxesData.map(item => {
      const [id, timestamp] = item;
      const sarCapture = isSarCapture(id);
      const cloudCover = getNonSarCloudCover(id, timestamp, cloudData.hourly);
      const cloudCoverColor = getCloudCoverColor(cloudCover);
      const spec = satSpecs[id] ? satSpecs[id] : {};
      return {
        id,
        timestamp,
        sarCapture,
        cloudCover,
        cloudCoverColor,
        resolution: Number(spec.resolution),
        spectralType: spec.spectral_range || "Unknown",
      };
    });

    return [...boundingBoxesDataWithCloudCover].sort((a, b) => {
      if (sortKey === "cloudCover") {
        const aCloud = a.cloudCover === null ? Number.POSITIVE_INFINITY : a.cloudCover;
        const bCloud = b.cloudCover === null ? Number.POSITIVE_INFINITY : b.cloudCover;
        if (aCloud !== bCloud) {
          return aCloud - bCloud;
        }
      }

      if (sortKey === "resolution") {
        const aResolution = Number.isFinite(a.resolution) ? a.resolution : Number.POSITIVE_INFINITY;
        const bResolution = Number.isFinite(b.resolution) ? b.resolution : Number.POSITIVE_INFINITY;
        if (aResolution !== bResolution) {
          return aResolution - bResolution;
        }
      }

      if (sortKey === "spectralType") {
        const spectralCompare = a.spectralType.localeCompare(b.spectralType);
        if (spectralCompare !== 0) {
          return spectralCompare;
        }
      }

      const aDate = new Date(a.timestamp.replace('+00:00', 'Z'));
      const bDate = new Date(b.timestamp.replace('+00:00', 'Z'));
      return aDate - bDate;
    });
  }, [boundingBoxesData, cloudData.hourly, sortKey]);
  
  
  return (
    <div className="table-container">
      <div className="table-toolbar">
        <label htmlFor="capture-sort">Sort by</label>
        <select
          id="capture-sort"
          value={sortKey}
          onChange={(event) => setSortKey(event.target.value)}
        >
          <option value="cloudCover">Best non-SAR cloud cover</option>
          <option value="resolution">Best resolution</option>
          <option value="spectralType">Spectral type</option>
        </select>
      </div>
      <table className="capture-table">
        <thead>
          <tr className="table-header">
            <th className="table-cell">Sat</th>
            <th className="table-cell">Resolution</th>
            <th className="table-cell">Spectral Type</th>
            <th className="table-cell">Non-SAR Cloud Cover</th>
            <th className="table-cell">Date-Time (UTC)</th>
          </tr>
        </thead>
        <tbody>
          {sortedBoundingBoxesData.map((item, index) => (
            <MemoizedTableRow
              key={index}
              item={item}
              index={index}
              handleRowMouseEnter={handleRowMouseEnter}
              handleRowMouseLeave={handleRowMouseLeave}
              cloudDataLoaded={cloudDataLoaded}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CaptureTable;

